import { IconNames } from '@blueprintjs/icons';
import { CONTRACT_RATE } from '@strata/constants';
import { DateFormats, formatDate } from '@strata/helper-functions';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import {
    type ColumnDef,
    type ColumnDefGeneric,
    type FilterCategory,
    GridWithFilters,
    SecondaryData,
    TimeFrameOptions,
} from '@strata/react-grid';
import { ClaimStatusIcon, Icon } from '@strata/react-status-icons';

import { ClaimGridActionsMenu } from 'components/claims/ClaimGridActionsMenu';
import { ClaimStatusRow, ClaimStatusRowHeader } from 'components/claims/ClaimStatusRow';
import { ClaimFrequencyTypeIcon } from 'components/shared/ClaimFrequencyTypeIcon';
import { SnoozeStatusIcon } from 'components/shared/SnoozeStatusIcon';
import { type ClaimSummary, useGetClaimsQuery } from 'store/api/medbillingApi';
import { useGetPayersQuery, useGetSupplierDetailsQuery } from 'store/api/referenceApi';
import { Grids } from 'store/grids/gridsSlice';
import { useGridState } from 'store/grids/gridsThunks';

const initialSortProperties = [{ id: 'submitted_date', desc: true }];

const timeFrameConfig = {
    key: 'submitted_date',
    title: 'Claim Time Frame',
    defaultValue: TimeFrameOptions.PAST_DAY,
    optionOverrideMap: { [TimeFrameOptions.PAST_DAY]: { text: 'Past 24 Hours' } },
};

const ResearchStatusFilter = {
    ONGOING: 'ONGOING',
    SNOOZED: 'SNOOZED',
    FOLLOW_UP: 'FOLLOW_UP',
    ALL: 'ALL',
};

const useFilterCategories = (
    filterValues?: Record<string, string[]>,
): FilterCategory<ClaimSummary>[] => {
    const { data: payers } = useGetPayersQuery();
    const payerMap = Object.fromEntries(
        (payers ?? []).map(({ payerId, payerName }) => [payerId, payerName]),
    );

    const filters: { key: keyof ClaimSummary; title: string }[] = [
        { key: 'product_service_display_name', title: 'Product' },
        { key: 'payer_id', title: 'Health Plan' },
        { key: 'status_999', title: '999 Status' },
        { key: 'status_277_ca', title: '277 CA Status' },
        { key: 'internal_claim_status', title: 'Claim Status' },
    ];
    return [
        {
            key: 'snooze_date',
            title: 'Research Status',
            useRadioButtons: true,
            defaultValue: [ResearchStatusFilter.ONGOING],
            customValues: [
                { text: 'Ongoing Research', value: ResearchStatusFilter.ONGOING },
                { text: 'Snoozed Research', value: ResearchStatusFilter.SNOOZED },
                { text: 'Ready for Follow-Up', value: ResearchStatusFilter.FOLLOW_UP },
                { text: 'All', value: ResearchStatusFilter.ALL },
            ],
        },
        ...filters.map((filter) => ({
            ...filter,
            customValues: (filterValues?.[filter.key] ?? []).map((value) => ({
                value,
                text: (() => {
                    if (filter.key === 'payer_id') {
                        return payerMap[value];
                    }
                    if (
                        ['status_999', 'status_277_ca', 'internal_claim_status'].includes(
                            filter.key,
                        )
                    ) {
                        return ClaimStatusIcon.StatusTextMap[
                            value as (typeof ClaimStatusIcon.Statuses)[keyof typeof ClaimStatusIcon.Statuses]
                        ];
                    }
                    return value;
                })(),
            })),
        })),
        {
            key: 'winningbid_rate',
            title: 'Servicing Rate',
            customValues: [
                { text: 'Contract', value: `${CONTRACT_RATE}` },
                { text: 'Non-Contract', value: '0' },
            ],
        },
    ];
};

/* eslint-disable react/prop-types */
const PayerCell: ColumnDefGeneric<ClaimSummary, 'payer_id'>['cell'] = ({ getValue }) => {
    const { data: payers } = useGetPayersQuery();
    return (
        <SecondaryData data={payers?.find(({ payerId }) => payerId === getValue())?.payerName} />
    );
};
const PharmacyCell: ColumnDefGeneric<ClaimSummary, 'supplier_npi'>['cell'] = ({ getValue }) => {
    const { data: supplierDetails } = useGetSupplierDetailsQuery(
        { supplierNpi: getValue() },
        { skip: !getValue() },
    );
    const hoverText = supplierDetails?.organizationName?.concat(
        ' (NPI: ',
        supplierDetails?.supplierNpi,
        ')',
    );
    return (
        <SecondaryData data={supplierDetails?.organizationName} customTooltipContent={hoverText} />
    );
};
/* eslint-enable react/prop-types */

const columns: ColumnDef<ClaimSummary>[] = [
    {
        accessorKey: 'snooze_date',
        header: () => (
            <Icon icon={IconNames.NOTIFICATIONS_SNOOZE} tooltipContent="Claim snooze indicator" />
        ),
        size: 45,
        cell: ({ getValue, row }) => (
            <SnoozeStatusIcon
                snoozeDate={getValue()}
                snoozeReason={row.original.snooze_reason}
                snoozeFollowUpDate={row.original.snooze_follow_up_date}
            />
        ),
    },
    {
        accessorKey: 'transaction_id',
        header: 'Transaction ID',
        size: 175,
    },
    {
        accessorKey: 'product_service_display_name',
        header: 'Product',
        size: 120,
    },
    {
        accessorKey: 'payer_id',
        header: 'Health Plan',
        size: 120,
        cell: PayerCell,
    },
    {
        accessorKey: 'supplier_npi',
        header: 'Pharmacy',
        size: 150,
        cell: PharmacyCell,
    },
    {
        accessorKey: 'notes_count',
        header: () => <Icon icon={IconNames.ANNOTATION} tooltipContent="Claim notes indicator" />,
        size: 45,
        cell: ({ getValue }) =>
            getValue() ? (
                <Icon icon={IconNames.ANNOTATION} tooltipContent="This claim has notes." />
            ) : null,
    },
    {
        accessorKey: 'claim_frequency_type',
        header: () => (
            <Icon icon={IconNames.CHANGES} tooltipContent="Revised and voided claim indicator" />
        ),
        size: 45,
        cell: ({ getValue }) => <ClaimFrequencyTypeIcon claimFrequencyType={getValue()} iconOnly />,
    },
    {
        accessorKey: 'start_date_of_service',
        header: 'Date of Service',
        size: 180,
        cell: ({ getValue, row }) => (
            <SecondaryData
                data={`${formatDate(getValue(), DateFormats.DATE)} – ${formatDate(row.original.end_date_of_service, DateFormats.DATE)}`}
            />
        ),
    },
    {
        accessorKey: 'submitted_date',
        header: 'Submission Date',
        size: 150,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
    {
        accessorKey: 'internal_claim_status',
        header: () => <ClaimStatusRowHeader />,
        size: 265,
        enableSorting: false,
        cell: ClaimStatusRow,
    },
    {
        accessorKey: 'claim_id',
        header: () => null,
        enableSorting: false,
        size: 48,
        cell: ({ row }) => <ClaimGridActionsMenu claim={row.original} />,
    },
];

export const ClaimsPage = () => {
    const {
        sort,
        search,
        activeFilters,
        filterPaneWidth,
        page,
        updateGridSort,
        updateGridSearch,
        updateGridActiveFilters,
        updateFilterPaneWidth,
        updateCurrentPage,
    } = useGridState(Grids.CLAIMS, initialSortProperties);

    const apiQueryParams = {
        page,
        searchText: search,
        activeFilters,
        sort,
    };
    const { data: firstPageData, isFetching: isFirstPageDataFetching } = useGetClaimsQuery({
        ...apiQueryParams,
        page: 0,
    });
    const { data: currentPageData, isFetching: isCurrentPageDataFetching } =
        useGetClaimsQuery(apiQueryParams);

    const filterCategories = useFilterCategories(firstPageData?.filters);

    return (
        <FormPageContainer>
            <GridWithFilters
                title="Claims"
                data={currentPageData?.claims}
                isLoading={isFirstPageDataFetching || isCurrentPageDataFetching}
                columns={columns}
                manualSorting
                sortProperties={sort}
                onSort={updateGridSort}
                enableSearch
                searchText={search}
                onSearch={updateGridSearch}
                timeFrameConfig={timeFrameConfig}
                filterCategories={filterCategories}
                activeFilters={activeFilters}
                onFilter={updateGridActiveFilters}
                filterPaneRenderWidth={filterPaneWidth}
                onFilterPaneResize={updateFilterPaneWidth}
                currentPage={page}
                onPageChange={updateCurrentPage}
                totalCount={firstPageData?.count}
                rowCssClass={(rowData) =>
                    rowData.resubmitted_transaction_id ? 'sds_claim-row--resubmitted' : undefined
                }
            />
        </FormPageContainer>
    );
};
